import type { EvidenciaOut } from "@/lib/api";
import { fmtBytes } from "@/lib/formato";

const fmtSize = fmtBytes;

// Predicados del estado de compresión (compartidos por la pestaña, la tarjeta y la fila).
const enCursoCompresion = (ev: EvidenciaOut) =>
  ev.estado_compresion === "pendiente" || ev.estado_compresion === "procesando";
const sinArchivoCompresion = (ev: EvidenciaOut) =>
  ev.estado_compresion === "error" || ev.estado_compresion === "excede_limite";

// Badge/chip/acciones según el estado de compresión (compartido card + fila).
// Qué significa cada chip lo explica la Ayuda de Evidencias (cero tooltips).
export default function EstadoCompresion({
  ev,
  onReintentar,
  onDescartar,
}: {
  ev: EvidenciaOut;
  onReintentar: () => void;
  onDescartar: () => void;
}) {
  const est = ev.estado_compresion;
  if (est === "pendiente" || est === "procesando") {
    return (
      <span className="inline-flex items-center gap-1.5 text-[10px] font-medium text-accent-400 bg-accent-500/10 border border-accent-500/25 rounded-full px-2 py-0.5 w-fit">
        <span className="w-2.5 h-2.5 rounded-full border-2 border-accent-400/30 border-t-accent-400 animate-spin" />
        Comprimiendo…
      </span>
    );
  }
  if (est === "listo" && ev.size_original_bytes) {
    const pct = Math.round((1 - ev.size_bytes / ev.size_original_bytes) * 100);
    return (
      <span className="inline-flex items-center gap-1 text-[10px] text-zinc-400 bg-ink-800 border border-ink-600 rounded px-1.5 py-0.5 w-fit font-mono">
        🗜️ {fmtSize(ev.size_original_bytes)} <span className="text-accent-500">→</span>{" "}
        <span className="text-accent-400 font-semibold">{fmtSize(ev.size_bytes)}</span>
        {pct > 0 ? ` · −${pct}%` : ""}
      </span>
    );
  }
  if (est === "error") {
    return (
      <div className="space-y-1">
        <span className="inline-flex items-center gap-1 text-[10px] font-medium text-red-300 bg-red-500/10 border border-red-500/30 rounded-full px-2 py-0.5 w-fit">
          ✕ Error al comprimir
        </span>
        <div className="flex flex-wrap gap-1.5">
          <button
            onClick={onReintentar}
            className="text-[10px] px-2 py-0.5 rounded border border-accent-500/40 text-accent-300 hover:bg-accent-500/10"
          >
            ↻ Reintentar
          </button>
          <button
            onClick={onDescartar}
            className="text-[10px] px-2 py-0.5 rounded border border-ink-600 text-zinc-400 hover:text-zinc-200"
          >
            Descartar
          </button>
        </div>
      </div>
    );
  }
  if (est === "excede_limite") {
    return (
      <div className="space-y-1">
        <span className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-full px-2 py-0.5 w-fit">
          ⚠ Muy pesado (&gt;200&nbsp;MB)
        </span>
        <p className="text-[10px] text-amber-300/80 leading-snug">
          El comprimido quedó en {fmtSize(ev.size_bytes)} y aún pasa de 200 MB. No se
          guardó aquí — súbelo por otro medio.
        </p>
        <button
          onClick={onDescartar}
          className="text-[10px] px-2 py-0.5 rounded border border-ink-600 text-zinc-400 hover:text-zinc-200 w-fit"
        >
          Descartar aviso
        </button>
      </div>
    );
  }
  return null;
}

export { enCursoCompresion, sinArchivoCompresion };
